import {getFallbackGameImage} from "@assets/images/fallbackGameImage";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {CarouselGameCard, HeadingCarousel, normalizeCarouselGame} from "@/components";
import {useAuthStore} from "@/store/useAuth.store";
import useGetGameCarousels from "../../../../../../../hooks/useGetGameCarousels";
import styles from "./game-promos.styles.module.scss";

/**
 * @typedef {Object} RecentPlayer
 * @property {string} [name]
 * @property {string} [alias]
 * @property {string} [avatar_url]
 * @property {string} [color]
 */

/**
 * @typedef {Object} PrizePool
 * @property {number} amount
 * @property {string} currency
 */

/**
 * @typedef {Object} Promo
 * @property {string} id
 * @property {string} title
 * @property {string} [subtitle]
 * @property {string} [badge_label]
 * @property {PrizePool} [prize_pool]
 * @property {string} [end_at]           Schedule end (existing field).
 * @property {string} [promo_ends_at]    Optional separate countdown end. Falls
 *                                       back to `end_at` if missing.
 * @property {number} [players_count]
 * @property {RecentPlayer[]} [recent_players]
 * @property {string} [cta_label]
 * @property {string} [cta_url]
 * @property {string} [documentation_url]
 * @property {Array<{id:string,name:string,thumbnail_url:string,provider:string,provider_game_id:string}>} [games]
 */

const TIME_LEFT_TICK_MS = 60_000;

/**
 * Compute a coarse "Xd Yh" / "Yh Zm" / "Zm" string for the time remaining
 * until `endAt`. Returns `null` when the date is missing, invalid or already
 * past so the caller can hide the block entirely.
 *
 * @param {string|undefined|null} endAt ISO timestamp from the backend.
 * @param {string} daysSuffix Localised single-letter days suffix (e.g. "d").
 * @param {string} hoursSuffix Localised single-letter hours suffix (e.g. "h").
 * @param {string} minutesSuffix Localised single-letter minutes suffix (e.g. "m").
 */
const formatTimeLeft = (endAt, daysSuffix, hoursSuffix, minutesSuffix) => {
  if (!endAt) return null;
  const endMs = new Date(endAt).getTime();
  if (Number.isNaN(endMs)) return null;
  const diff = endMs - Date.now();
  if (diff <= 0) return null;
  const totalMinutes = Math.floor(diff / 60_000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return `${days}${daysSuffix} ${hours}${hoursSuffix}`;
  }
  if (hours > 0) {
    return `${hours}${hoursSuffix} ${minutes}${minutesSuffix}`;
  }
  return `${minutes}${minutesSuffix}`;
};

/**
 * Format a `{ amount, currency }` prize pool into a user-friendly string.
 * Returns `null` when the prize pool is absent or malformed so the caller can
 * hide the stat instead of rendering a placeholder.
 */
const formatPrizePool = (prizePool, locale) => {
  if (!prizePool || prizePool.amount == null || !prizePool.currency) {
    return null;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: prizePool.currency,
      maximumFractionDigits: 0,
    }).format(prizePool.amount);
  } catch {
    // Unknown currency code → fall back to "<amount> <code>"
    return `${prizePool.amount.toLocaleString(locale)} ${prizePool.currency}`;
  }
};

/**
 * Format a players count with locale-aware thousand separators.
 */
const formatPlayersCount = (count, locale) => {
  if (count == null || Number.isNaN(Number(count))) return null;
  return Number(count).toLocaleString(locale);
};

const getPromoPrizeAndTime = (promo, locale, {daysSuffix, hoursSuffix, minutesSuffix}, fallback) => {
  const countdownTarget = promo.promo_ends_at || promo.end_at;
  return {
    prizePool: formatPrizePool(promo.prize_pool, locale) ?? fallback,
    timeLeft: formatTimeLeft(countdownTarget, daysSuffix, hoursSuffix, minutesSuffix) ?? fallback,
  };
};

const PromoPrizeAndTime = ({prizePool, timeLeft, players, t}) => (
  <div className={styles.heroStats}>
    <div className={styles.heroStat}>
      <span className={styles.heroStatIcon}>💰</span>
      <div className={styles.heroStatInfo}>
        <span className={styles.heroStatLabel}>{t("promoPrizePool")}</span>
        <span className={styles.heroStatValue}>{prizePool}</span>
      </div>
    </div>
    <div className={styles.heroStatDivider} />
    <div className={styles.heroStat}>
      <span className={styles.heroStatIcon}>⏳</span>
      <div className={styles.heroStatInfo}>
        <span className={styles.heroStatLabel}>{t("promoTimeLeft")}</span>
        <span className={styles.heroStatValue}>{timeLeft}</span>
      </div>
    </div>
    {players && (
      <>
        <div className={styles.heroStatDivider} />
        <div className={styles.heroStat}>
          <span className={styles.heroStatIcon}>👥</span>
          <div className={styles.heroStatInfo}>
            <span className={styles.heroStatLabel}>{t("promoPlayers")}</span>
            <span className={styles.heroStatValue}>{players}</span>
          </div>
        </div>
      </>
    )}
  </div>
);

export const GamePromos = () => {
  const {t, i18n} = useTranslation();
  const {promos} = useGetGameCarousels();
  const tournamentPromos = useMemo(
    () => promos.filter(promo => String(promo.placement || "").toLowerCase() === "tournament"),
    [promos]
  );
  const navigate = useNavigate();
  const authStatus = useAuthStore(state => state.authStatus);
  const handleShowAuthModal = useAuthStore(state => state.handleShowAuthModal);

  /** @type {[null | { promoId: string, alias: string }, Function]} */
  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null);

  // Re-render once a minute so the "time left" labels stay fresh without us
  // touching dayjs / setInterval inside every render.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), TIME_LEFT_TICK_MS);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!activePopup) return;
    const handleClickOutside = e => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setActivePopup(null);
      }
    };
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [activePopup]);

  const handlePlayerClick = useCallback((promoId, alias) => {
    if (!alias) return;
    setActivePopup(prev => (prev?.promoId === promoId && prev?.alias === alias ? null : {promoId, alias}));
  }, []);

  const handleGameClick = useCallback(
    game => {
      if (authStatus === "authenticated") {
        navigate(`/games/${game.provider}/${game.provider_game_id}`);
      } else {
        handleShowAuthModal();
      }
    },
    [authStatus, navigate, handleShowAuthModal]
  );

  const handleImageError = useCallback(e => {
    e.target.onerror = null;
    e.target.src = getFallbackGameImage();
  }, []);

  const handleCtaClick = useCallback(url => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }, []);

  const daysSuffix = t("promoTimeLeftDays", {defaultValue: "d"});
  const hoursSuffix = t("promoTimeLeftHours", {defaultValue: "h"});
  const minutesSuffix = t("promoTimeLeftMinutes", {defaultValue: "m"});

  const firstPromo = tournamentPromos[0];

  const statsFallback = t("promoStatsTBD", {defaultValue: "Por confirmar"});
  const timeSuffixes = {daysSuffix, hoursSuffix, minutesSuffix};

  const promoPrizeAndTimeById = useMemo(() => {
    const map = new Map();
    for (const promo of tournamentPromos) {
      map.set(promo.id, getPromoPrizeAndTime(promo, i18n.language, timeSuffixes, statsFallback));
    }
    return map;
    // `now` keeps countdown labels fresh each minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentPromos, i18n.language, daysSuffix, hoursSuffix, minutesSuffix, statsFallback, now]);

  if (!tournamentPromos.length || !firstPromo) {
    return null;
  }

  const sectionSubtitle = firstPromo.subtitle || t("dontMissTournaments");

  return (
    <section className={styles.promoArena}>
      <HeadingCarousel
        title={t("dontMiss")}
        subtitle={sectionSubtitle}
        icon="🔥"
      />

      <div className={styles.promoList}>
        {tournamentPromos.map((promo, index) => {
          const isFeatured = index === 0;
          const promoBadge = isFeatured ? promo.badge_label || t("promoFeatured") : promo.badge_label;
          const promoCtaLabel = promo.cta_label || t("promoLearnMore");
          const promoCtaUrl = promo.cta_url || promo.documentation_url || null;
          const prizeAndTime = promoPrizeAndTimeById.get(promo.id);
          const playersCount = formatPlayersCount(promo.players_count, i18n.language);
          const recentPlayers = Array.isArray(promo.recent_players) ? promo.recent_players : [];
          const selectedPlayer =
            activePopup?.promoId === promo.id ? recentPlayers.find(p => p?.alias === activePopup.alias) || null : null;

          return (
            <div
              key={promo.id}
              className={styles.promoListItem}
            >
              <div className={styles.heroCard}>
                <div className={styles.heroAccentLine} />
                <div className={styles.heroGlow} />

                <div className={styles.heroBody}>
                  {promoBadge && (
                    <span className={styles.heroBadge}>
                      <span className={styles.heroBadgeDot} />
                      {promoBadge}
                    </span>
                  )}

                  <h2 className={styles.heroTitle}>{promo.title}</h2>

                  <div className={styles.heroGamesWrap}>
                    <div className={styles.heroGamesTrack}>
                      {(promo.games ?? []).map(game => {
                        const normalized = normalizeCarouselGame(game);
                        return (
                          <CarouselGameCard
                            key={game.id}
                            name={normalized.name}
                            icon={normalized.icon}
                            providerLabel={normalized.providerLabel}
                            onClick={() => handleGameClick(game)}
                          />
                        );
                      })}
                    </div>
                  </div>

                  {prizeAndTime && (
                    <PromoPrizeAndTime
                      prizePool={prizeAndTime.prizePool}
                      timeLeft={prizeAndTime.timeLeft}
                      players={playersCount}
                      t={t}
                    />
                  )}

                  {promoCtaUrl && (
                    <div className={styles.heroCtas}>
                      <button
                        type="button"
                        className={styles.heroPrimaryBtn}
                        onClick={() => handleCtaClick(promoCtaUrl)}
                      >
                        {promoCtaLabel}
                      </button>
                    </div>
                  )}

                  {recentPlayers.length > 0 && (
                    <div className={styles.playersWrap}>
                      <span className={styles.playersLabel}>👥 {t("promoRecentPlayers")}</span>
                      <div className={styles.playersTrack}>
                        {recentPlayers.map((player, i) => {
                          const pillKey = player?.alias || `recent-player-${promo.id}-${i}`;
                          const pillColor = player?.color || "#00CED1";
                          const isSelected = activePopup?.promoId === promo.id && player?.alias === activePopup.alias;
                          return (
                            <button
                              key={pillKey}
                              type="button"
                              className={`${styles.playerPill} ${i === 0 ? styles.playerPillNew : ""} ${isSelected ? styles.playerPillSelected : ""}`}
                              style={{animationDelay: `${i * 0.15}s`}}
                              onClick={() => handlePlayerClick(promo.id, player?.alias)}
                              disabled={!player?.alias}
                            >
                              {player?.avatar_url && (
                                <span
                                  className={styles.playerAvatar}
                                  style={{borderColor: pillColor}}
                                >
                                  <img
                                    src={player.avatar_url}
                                    alt={player?.name || player?.alias || ""}
                                    className={styles.playerAvatarImg}
                                    onError={handleImageError}
                                  />
                                </span>
                              )}
                              {player?.name || player?.alias}
                            </button>
                          );
                        })}
                      </div>
                      {selectedPlayer && (
                        <div
                          className={styles.playerActionBar}
                          ref={popupRef}
                        >
                          <div className={styles.actionBarPlayer}>
                            {selectedPlayer.avatar_url && (
                              <img
                                src={selectedPlayer.avatar_url}
                                alt={selectedPlayer.name || selectedPlayer.alias}
                                className={styles.actionBarAvatar}
                                onError={handleImageError}
                              />
                            )}
                            <span className={styles.actionBarName}>{selectedPlayer.name || selectedPlayer.alias}</span>
                          </div>
                          <div className={styles.actionBarBtns}>
                            <button
                              type="button"
                              className={styles.actionBarBtn}
                              onClick={() => {
                                setActivePopup(null);
                                navigate(`/u/${selectedPlayer.alias}`);
                              }}
                            >
                              👤 {t("promoViewProfile")}
                            </button>
                            <button
                              type="button"
                              className={styles.actionBarBtn}
                              onClick={() => {
                                setActivePopup(null);
                                navigate(`/u/${selectedPlayer.alias}`);
                              }}
                            >
                              <span className={styles.actionBarStoryDot} />
                              {t("promoViewStories")}
                            </button>
                            <button
                              type="button"
                              className={styles.actionBarBtn}
                              onClick={() => {
                                setActivePopup(null);
                                navigate("/user/contacts");
                              }}
                            >
                              ➕ {t("promoAddContact")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
